// Only focused tabs publish presence. A lease prevents a crashed tab from suppressing alerts forever.
const PRESENCE_REFRESH_MS = 5_000;
const PRESENCE_TTL_MS = 15_000;

export function createThreadNotificationPresence(
  email: string,
  getFocusedPath: () => string | null,
) {
  const prefix = `operators:thread-notification-presence:${encodeURIComponent(email)}:`;
  const key = `${prefix}${crypto.randomUUID()}`;
  let published = false;

  const clear = () => {
    try {
      if (published) localStorage.removeItem(key);
      published = false;
    } catch {
      // The lease expires even when storage is unavailable during cleanup.
    }
  };
  const refresh = () => {
    const path = getFocusedPath();
    if (!path) {
      clear();
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({ path, updatedAt: Date.now() }));
      published = true;
    } catch {
      // Delivery still checks this tab directly when browser storage is unavailable.
    }
  };
  const isThreadViewed = (threadId: string) => {
    const path = `/operators/${encodeURIComponent(threadId)}`;
    if (getFocusedPath() === path) return true;
    try {
      for (let index = 0; index < localStorage.length; index++) {
        const otherKey = localStorage.key(index);
        if (!otherKey?.startsWith(prefix)) continue;
        try {
          const entry: unknown = JSON.parse(localStorage.getItem(otherKey) ?? "null");
          if (!entry || typeof entry !== "object") continue;
          const { path: otherPath, updatedAt } = entry as Record<string, unknown>;
          if (
            otherPath === path &&
            typeof updatedAt === "number" &&
            Date.now() - updatedAt >= 0 &&
            Date.now() - updatedAt < PRESENCE_TTL_MS
          )
            return true;
        } catch {
          // Ignore malformed records from another tab.
        }
      }
    } catch {
      // Reading shared presence must not interrupt thread updates.
    }
    return false;
  };

  window.addEventListener("focus", refresh);
  window.addEventListener("blur", refresh);
  window.addEventListener("pageshow", refresh);
  window.addEventListener("pagehide", clear);
  document.addEventListener("visibilitychange", refresh);
  const interval = setInterval(refresh, PRESENCE_REFRESH_MS);
  refresh();

  return {
    refresh,
    isThreadViewed,
    dispose: () => {
      clearInterval(interval);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("blur", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("pagehide", clear);
      document.removeEventListener("visibilitychange", refresh);
      clear();
    },
  };
}
